import { Effect } from 'effect';

/** Tiny classnames joiner — avoids pulling tailwind-merge for simple conditionals. */
export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * True when keyboard focus sits in a modal dialog other than `own` — a layer
 * stacked on top (the host's command palette). A document-level key handler
 * checks this so it does not act on keys that belong to that layer.
 */
export function focusIsInAnotherModal(own: Element | null): boolean {
  const modal = document.activeElement?.closest('[role="dialog"][aria-modal="true"]');
  return modal != null && modal !== own;
}

const MODAL_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Focus and keyboard for a modal while it is up: `initial` takes the focus,
 * Escape calls `onClose`, and Tab stays inside `panel`. The returned cleanup
 * gives the focus back, so it fits a mount effect.
 */
export function holdModalFocus(
  panel: HTMLElement | null,
  initial: HTMLElement | null,
  onClose: () => void,
): () => void {
  const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  initial?.focus();
  // Handled on the document: a click on plain text inside the dialog leaves
  // focus on the body, where a handler on the panel would never fire.
  const onKeyDown = (event: KeyboardEvent) => {
    // A dialog opened over this one (the host's command palette) owns the
    // keyboard: it has handled the key already, or it holds the focus.
    // Acting here too would close both layers on one Escape and pull Tab
    // out from under it.
    if (event.defaultPrevented || focusIsInAnotherModal(panel)) return;
    if (event.key === 'Escape') {
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(panel?.querySelectorAll<HTMLElement>(MODAL_FOCUSABLE) ?? []);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!panel || !first || !last) return;
    const [edge, wrapTo] = event.shiftKey ? [first, last] : [last, first];
    const active = document.activeElement;
    if (active === edge || !panel.contains(active)) {
      event.preventDefault();
      wrapTo.focus();
    }
  };
  document.addEventListener('keydown', onKeyDown);
  return () => {
    document.removeEventListener('keydown', onKeyDown);
    previous?.focus();
  };
}

export function formatBytes(bytes: number): string {
  return Effect.runSync(formatBytesEffect(bytes));
}

export function formatBytesEffect(bytes: number): Effect.Effect<string> {
  return Effect.sync(() => {
    if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB'];
    let value = bytes;
    let unit = 'B';
    for (const next of units) {
      if (value < 1024) break;
      value /= 1024;
      unit = next;
    }
    return `${value >= 100 ? Math.round(value) : value.toFixed(1)} ${unit}`;
  });
}

/** pdf-lib save() returns Uint8Array backed by a larger buffer; copy to an exact BlobPart. */
export function uint8ArrayToBlobPart(bytes: Uint8Array): BlobPart {
  return Effect.runSync(uint8ArrayToBlobPartEffect(bytes));
}

export function uint8ArrayToBlobPartEffect(bytes: Uint8Array): Effect.Effect<BlobPart> {
  return Effect.sync(() => {
    const copy = new Uint8Array(bytes.byteLength);
    copy.set(bytes);
    return copy.buffer as ArrayBuffer;
  });
}
